import axios from 'axios'
import ENDPOINTS from './../api/constant/index'
import PAGES_LINKS from './../pages/constants/index'
import { defaultLanguage } from './../translations/constant/index'
import CONSTANTS from './constants'
import { decrypt, encrypt } from './security'
import { validateDescriptionIntegrity } from './descriptionIntegrity'
import { clearLoginInfo, getCookieValue, setCookie } from './utils'
import { refreshCorporateAccessToken, renewObject } from './corporateAxios'

const api = '/api/'
const blackedListURL = [
  'CustomerInfo/UploadUserPic',
  'SessionManagement/AddSessionVariables',
  'SessionManagement/GetSessionVariables',
  'Payment/Bundle/CreateJawwalPayPayment', // Exclude from deviceId auto-injection since we send DeviceID
  'DirectPay/ProcessPayment', // directPayProcessPayment controls whether this endpoint receives a device ID
]
const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}${api}`,
})
const authenticatedAxios = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}${api}`,
})
const loginAxios = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}${api}`,
})

const customerCode = JSON.parse(localStorage.getItem('customerCode'))

const descriptionIntegrityEndpoints = [
  'offers/getoffers',
  'offers/getfixedlinehotoffers',
  'bundles/getcampaigncategories',
  'offers/getofferdetails',
  'offers/getavailablepopup',
  'offers/getcrosssellingoffers',
  'bundles/getaccountbundlesbycattype',
]

const shouldValidateDescription = (url = '') => {
  if (!url) {
    return false
  }
  const normalizedUrl = url.toLowerCase()
  return descriptionIntegrityEndpoints.some((endpoint) =>
    normalizedUrl.includes(endpoint)
  )
}

const runDescriptionIntegrityCheck = (response) => {
  try {
    const requestUrl = response?.config?.url
    if (!shouldValidateDescription(requestUrl)) {
      return
    }
    const payload = response?.data?.result ?? response?.data
    if (!payload || typeof payload !== 'object') {
      return
    }
    const { invalidCount } = validateDescriptionIntegrity(payload)
    if (invalidCount > 0) {
      console.warn(
        `Description integrity failed for ${invalidCount} item(s) in ${requestUrl}`
      )
    }
  } catch (error) {
    console.error('Description integrity validation error', error)
  }
}

const refreshAccessToken = async () => {
  const { data: tokens } = await authenticatedAxios.post(
    `${ENDPOINTS.auth}${ENDPOINTS.renewToken}`,
    {
      OSType: '0',
    }
  )
  if (tokens?.result?.token) {
    setCookie({
      cname: 'token',
      cvalue: encrypt(tokens.result.token),
      expires: 30,
      cookieExpireTimeType: CONSTANTS.DAYS,
    })
    setCookie({
      cname: 'refreshToken',
      cvalue: encrypt(tokens.result.refreshToken),
      expires: 30,
      cookieExpireTimeType: CONSTANTS.DAYS,
    })
  }
  return tokens
}

const logout = async () => {
  const { data } = await authenticatedAxios.post(
    `${ENDPOINTS.auth}${ENDPOINTS.logout}`
  )
  return data
}
export const addTheLogOutBehavior = () => {
  clearLoginInfo()
  renewObject.reset()
  return window.location.assign(PAGES_LINKS.main)
}
axiosInstance.interceptors.request.use(async (config) => {
  config.headers = {
    'Content-Type': 'application/json',
    Lang: !!localStorage.getItem('lang')
      ? localStorage.getItem('lang')
      : defaultLanguage,
    Channel: 'Website',
  }
  if (config.method === 'post' && !blackedListURL.includes(config.url)) {
    config.data = {
      ...(typeof config.data === 'string'
        ? JSON.parse(config.data)
        : config.data),
      deviceId: localStorage.getItem('deviceId'),
    }
  }
  return config
})

authenticatedAxios.interceptors.request.use(async (config) => {
  const token = decrypt(getCookieValue('token'))
  if (!token) {
    throw new authenticatedAxios.Cancel('Operation canceled by the user.')
  }
  config.headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    Lang: !!localStorage.getItem('lang')
      ? localStorage.getItem('lang')
      : defaultLanguage,
    Channel: 'Website',
  }
  if (config.method === 'post' && !blackedListURL.includes(config.url)) {
    config.data = {
      ...(typeof config.data === 'string'
        ? JSON.parse(config.data)
        : config.data),
      deviceId: localStorage.getItem('deviceId'),
    }
  }
  if (
    !!decrypt(getCookieValue('switchToken')) &&
    config.url !== `${ENDPOINTS.auth}${ENDPOINTS.renewToken}`
  ) {
    config.headers = {
      ...config.headers,
      'switch-token': decrypt(getCookieValue('switchToken')),
    }
  } else if (
    !!decrypt(getCookieValue('switchToken')) &&
    config.url === `${ENDPOINTS.auth}${ENDPOINTS.renewToken}`
  ) {
    config.headers = {
      ...config.headers,
      'switch-token': decrypt(getCookieValue('switchToken')),
      'Refresh-Token': decrypt(getCookieValue('refreshToken')),
    }
    config._renewRequest = true
  }
  if (config.url === `${ENDPOINTS.auth}${ENDPOINTS.renewToken}`) {
    config.headers = {
      ...config.headers,
      'Refresh-Token': decrypt(getCookieValue('refreshToken')),
    }
    config._renewRequest = true
  }

  return config
})

authenticatedAxios.interceptors.response.use(
  (response) => {
    runDescriptionIntegrityCheck(response)
    return response
  },
  async function (error) {
    const originalRequest = error.config
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (originalRequest._renewRequest) {
        renewObject.processQueue(null)
        addTheLogOutBehavior()
        return
      }
      if (renewObject.isRefreshing) {
        return new Promise(function (resolve, reject) {
          renewObject.failedQueue.push({ resolve, reject })
        })
          .then(() => {
            return authenticatedAxios(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      renewObject.isRefreshing = true

      let cookie

      if (customerCode) {
        cookie = !decrypt(getCookieValue('customer-code-token'))
      } else {
        cookie =
          !decrypt(getCookieValue('corp-token')) &&
          !decrypt(getCookieValue('paltel-corp-token'))
      }

      const tokens = cookie
        ? await refreshAccessToken()
        : await refreshCorporateAccessToken()
      if (tokens?.result?.token) {
        authenticatedAxios.defaults.headers[
          'Authorization'
        ] = `Bearer ${tokens.result.token}`
        originalRequest.headers[
          'Authorization'
        ] = `Bearer ${tokens.result.token}`
        renewObject.processQueue(null, tokens.result.token)
        return authenticatedAxios(originalRequest)
      } else {
        renewObject.processQueue(null)
        addTheLogOutBehavior()
      }
    }

    if (error.response && error.response.status === 403 && !originalRequest._retry) {
      //TODO: add a pop up with a logout button
      addTheLogOutBehavior()
    }
    return Promise.reject(error)
  }
)

loginAxios.interceptors.request.use(async (config) => {
  config.headers = {
    'Content-Type': 'application/json',
    Lang: !!localStorage.getItem('lang')
      ? localStorage.getItem('lang')
      : defaultLanguage,
    Channel: 'Website',
  }
  if (config.method === 'post' && !blackedListURL.includes(config.url)) {
    config.data = {
      ...(typeof config.data === 'string'
        ? JSON.parse(config.data)
        : config.data),
    }
  }
  return config
})

export { axiosInstance, authenticatedAxios, loginAxios, logout }
// eslint-disable-next-line prettier/prettier
